// eslint-disable-next-line
import { useSnapshot as useSnapshotForReal } from "valtio";
import { IS_DEV } from "@/__main__/constants.mjs";
import { dataModelKey, getWithModel } from "@/__main__/data-model.mjs";

// This weakset is needed to prevent cycles, and also snapshots themselves
// are not extensible.
const accessProxySet = new WeakSet();

type Snapshot<T extends object> = ReturnType<typeof useSnapshotForReal<T>>;
type Options = Parameters<typeof useSnapshotForReal>[1];

export function useSnapshot<T extends object>(
  obj: T,
  options?: Options,
): Snapshot<T> {
  const snapshot = useSnapshotForReal(obj, options);
  return accessProxy(snapshot);
}

function accessProxy<T>(value: T): T {
  if (!value || typeof value !== "object" || !IS_DEV) {
    return value;
  }

  accessProxySet.add(value);

  const model = value[dataModelKey];
  let getter;
  if (model) {
    getter = getWithModel(model);
  }

  return new Proxy(value, {
    get(target, key, receiver) {
      if (getter) {
        return getter(target, key);
      }
      if (!accessProxySet.has(target)) {
        return accessProxy(target[key]);
      }
      return Reflect.get(target, key, receiver);
    },
  });
}
